import {
  countsInAggregates,
  currentBucket,
  fmt,
  isPlacedInTarget,
  type SeparationState,
} from '@integrate/core';
import { Section } from './sections.js';

/**
 * How much of the carve-out has a home in the designed organisation.
 *
 * Ported from `targetProgressHtml` (orgchart.html:4667). It appears on three panels — Perimeter,
 * Transfers and Target — because the question "have we placed everyone yet" is the same question
 * wherever you are standing, and it was duplicated byte-for-byte in two of them before this.
 *
 * Two things about the population, both deliberate.
 *
 * It counts EMPLOYEES AND BUILD-UPS (`realPop()`): a planned hire occupies a box in the target org like
 * anybody else, and a blueprint that ignored them would be short by exactly the positions the carve-out
 * is being built to fill.
 *
 * And it counts `countsInAggregates`, not `countsInHeadcount` — so departed people are excluded. That
 * was a real disagreement until now: this line used the wider predicate while `computeTargetRollup`,
 * the stage bridge and every group card on the chart use the narrower one, so the moment the progress
 * line and the rollup table sat in the same panel they differed by the number of departed carve-out
 * people with nothing to explain the gap. The rollup is the side to match: somebody who has left the
 * company cannot be placed into the organisation being designed.
 */
export function TargetProgress({
  state,
  scenarioName,
}: {
  state: SeparationState;
  /** Placements are per-scenario, so the count is only meaningful attributed to one. */
  scenarioName?: string;
}): JSX.Element {
  const carve = [...state.employees, ...state.design.buildUps].filter(
    (p) => currentBucket(state.design, p.id) === 'Carve-out' && countsInAggregates(p),
  );
  const placed = carve.filter((p) => isPlacedInTarget(state, p)).length;
  const unplaced = carve.length - placed;

  return (
    <Section
      testId="target-progress"
      title={
        <>
          Target org placement{' '}
          <span className="pill">
            {placed}/{carve.length}
          </span>
          {scenarioName && (
            <span className="pill" data-testid="target-scenario-label">
              {scenarioName}
            </span>
          )}
        </>
      }
    >
      <div className="sp-row">
        <span className="k">Carve-out people placed</span>
        <span className="v" data-testid="target-placed">
          {fmt(placed)} / {fmt(carve.length)}
        </span>
      </div>
      {unplaced > 0 && (
        <p className="muted" data-testid="target-unplaced">
          {fmt(unplaced)} still unplaced. Unplaced people are not drawn in the target view at all — the
          blueprint shows the organisation as designed, so somebody with no unit has nowhere to appear.
        </p>
      )}
    </Section>
  );
}
